/**
 * The date range every analytics surface shares.
 *
 * The picker in the top bar is the only writer; dashboards, analytics, planner and the
 * inbox read it. It persists so reopening the console lands on the same window.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { dayjs, previousRange, toIsoDate } from './format'
import type { IsoDate } from './format'

export type RangePresetId =
  | 'last7'
  | 'last28'
  | 'last30'
  | 'last90'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom'

export interface DateRange {
  from: IsoDate
  to: IsoDate
  preset: RangePresetId
}

/**
 * A preset carries no label. The word beside it is a catalogue key, resolved at render time by
 * `components/ui/DateRangePicker`'s `PRESET_KEY` map — an English `label` sitting here would be a
 * second, untranslated source for the same word, and the first thing a future renderer reached
 * for. Adding a preset means adding its key to that map, which will not compile until you do.
 */
export interface RangePreset {
  id: Exclude<RangePresetId, 'custom'>
  resolve: () => { from: IsoDate; to: IsoDate }
}

const startOfToday = () => dayjs().startOf('day')

export const RANGE_PRESETS: readonly RangePreset[] = [
  {
    id: 'last7',
    resolve: () => ({
      from: toIsoDate(startOfToday().subtract(6, 'day')),
      to: toIsoDate(startOfToday()),
    }),
  },
  {
    id: 'last28',
    resolve: () => ({
      from: toIsoDate(startOfToday().subtract(27, 'day')),
      to: toIsoDate(startOfToday()),
    }),
  },
  {
    id: 'last30',
    resolve: () => ({
      from: toIsoDate(startOfToday().subtract(29, 'day')),
      to: toIsoDate(startOfToday()),
    }),
  },
  {
    id: 'last90',
    resolve: () => ({
      from: toIsoDate(startOfToday().subtract(89, 'day')),
      to: toIsoDate(startOfToday()),
    }),
  },
  {
    id: 'thisMonth',
    resolve: () => ({
      from: toIsoDate(dayjs().startOf('month')),
      to: toIsoDate(startOfToday()),
    }),
  },
  {
    id: 'lastMonth',
    resolve: () => {
      const previous = dayjs().subtract(1, 'month')
      return {
        from: toIsoDate(previous.startOf('month')),
        to: toIsoDate(previous.endOf('month')),
      }
    },
  },
]

export const resolvePreset = (id: Exclude<RangePresetId, 'custom'>): DateRange => {
  const preset = RANGE_PRESETS.find((candidate) => candidate.id === id) ?? RANGE_PRESETS[1]
  if (!preset) {
    const to = toIsoDate(startOfToday())
    return { from: to, to, preset: 'last28' }
  }
  const { from, to } = preset.resolve()
  return { from, to, preset: preset.id }
}

export const DEFAULT_RANGE: DateRange = resolvePreset('last28')

const STORE_KEY = 'pd.range'

const readStoredRange = (): DateRange => {
  try {
    const raw = window.localStorage.getItem(STORE_KEY)
    if (!raw) return DEFAULT_RANGE
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_RANGE
    const candidate = parsed as Partial<DateRange>
    if (candidate.preset && candidate.preset !== 'custom') {
      // Relative presets must be re-resolved against today, never restored verbatim.
      return resolvePreset(candidate.preset)
    }
    if (typeof candidate.from === 'string' && typeof candidate.to === 'string') {
      return { from: candidate.from, to: candidate.to, preset: 'custom' }
    }
    return DEFAULT_RANGE
  } catch {
    return DEFAULT_RANGE
  }
}

export interface RangeContextValue {
  range: DateRange
  /** The equal-length window immediately before `range` — what every delta compares against. */
  comparison: { from: IsoDate; to: IsoDate }
  setRange: (next: DateRange) => void
  setPreset: (id: Exclude<RangePresetId, 'custom'>) => void
  setCustom: (from: IsoDate, to: IsoDate) => void
}

const RangeContext = createContext<RangeContextValue | null>(null)

export const RangeProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [range, setRangeState] = useState<DateRange>(readStoredRange)

  const setRange = useCallback((next: DateRange) => {
    setRangeState(next)
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(next))
    } catch {
      // Storage unavailable — the range still applies for this session.
    }
  }, [])

  const setPreset = useCallback(
    (id: Exclude<RangePresetId, 'custom'>) => {
      setRange(resolvePreset(id))
    },
    [setRange],
  )

  const setCustom = useCallback(
    (from: IsoDate, to: IsoDate) => {
      const ordered = dayjs(from).isAfter(dayjs(to)) ? { from: to, to: from } : { from, to }
      setRange({ ...ordered, preset: 'custom' })
    },
    [setRange],
  )

  const value = useMemo<RangeContextValue>(
    () => ({
      range,
      comparison: previousRange(range.from, range.to),
      setRange,
      setPreset,
      setCustom,
    }),
    [range, setRange, setPreset, setCustom],
  )

  return <RangeContext.Provider value={value}>{children}</RangeContext.Provider>
}

export const useDateRange = (): RangeContextValue => {
  const ctx = useContext(RangeContext)
  if (!ctx) throw new Error('useDateRange must be used inside <RangeProvider>.')
  return ctx
}
